import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import { array, arrayOf, bool, func, oneOfType, shape, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components';
import { useSafeState } from 'hooks';

import 'assets/styles/components/tables/_react-table.scss';

const ReactTable = ({ columns, data, pagination = false, handleRowClick = null, className = '' }) => {
  const [sorting, setSorting] = useSafeState([]);

  const table = useReactTable({
    columns: columns,
    data: data,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const { t } = useTranslation();

  const columnSortingButton = (column) => {
    const iconName = column.getIsSorted() === 'desc' ? 'angle-up' : 'angle-down';

    return (
      <button
        type="button"
        className={clsx('sort-btn', column.getCanSort() && 'sortable', column.getIsSorted() && 'sorted')}
        onClick={column.getToggleSortingHandler()}
      >
        <span>{column.columnDef.header}</span>
        <span className="sr-only">{t(`tables.sort.${column.getNextSortingOrder()}`)}</span>
        <Icon name={iconName} />
      </button>
    );
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className={clsx('react-table', className)}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={`header-group-${headerGroup.id}`}>
                {headerGroup.headers.map((header) => (
                  <th className={`cell-${header.id}`} key={`header-${header.id}`}>
                    {header.column.getCanSort() ? (
                      columnSortingButton(header.column)
                    ) : (
                      <span>{header.column.columnDef.header}</span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={`row-${row.id}`} onClick={() => handleRowClick && handleRowClick(row.original)}>
                {row.getVisibleCells().map((cell) => (
                  <td className={`cell-${cell.column.id}`} key={`cell-${cell.id}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="table-pagination">
          <button
            type="button"
            className="button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <Icon name="chevron-right" classes="rotate-180 h-3 w-3" />
            <Icon name="chevron-right" classes="rotate-180 h-3 w-3 -ml-[0.4rem]" />
          </button>

          <button
            type="button"
            className="button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <Icon name="chevron-right" classes="rotate-180 h-3 w-3" />
          </button>

          <span className="text-sm lg:text-base">
            {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
          </span>

          <button className="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            <Icon name="chevron-right" classes="h-3 w-3" />
          </button>

          <button
            type="button"
            className="button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <Icon name="chevron-right" classes="h-3 w-3" />
            <Icon name="chevron-right" classes="h-3 w-3 -ml-[0.4rem]" />
          </button>
        </div>
      )}
    </>
  );
};

ReactTable.propTypes = {
  columns: arrayOf(
    shape({
      accessorKey: string.isRequired,
      header: string.isRequired,
      footer: string,
      cell: func,
      enableSorting: bool,
      sortFn: oneOfType([string, func])
    })
  ).isRequired,
  data: array.isRequired,
  pagination: bool,
  handleRowClick: func,
  className: string
};

export default ReactTable;
